import { useCallback, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { uploadTranscription, type UploadResult } from "../api";
import { fmtBytes } from "../format";

interface Props {
  /** Called after a single upload completes (navigate to it). */
  onUploaded: (id: string) => void;
  /** Called whenever the library may have changed (multi-upload, dedup). */
  onChanged?: () => void;
}

type ItemState = "waiting" | "uploading" | "done" | "duplicate" | "error";

interface QueueItem {
  key: string;
  file: File;
  state: ItemState;
  progress: number;
  result?: UploadResult;
  error?: string;
}

let keySeq = 0;

export function UploadDropzone({ onUploaded, onChanged }: Props) {
  const [queue, setQueue] = useState<QueueItem[]>([]);
  const [drag, setDrag] = useState(false);
  const busyRef = useRef(false);

  const patch = (key: string, partial: Partial<QueueItem>) => {
    setQueue((q) => q.map((it) => (it.key === key ? { ...it, ...partial } : it)));
  };

  const pump = useCallback(async (items: QueueItem[]) => {
    if (busyRef.current) return;
    busyRef.current = true;
    let uploadedCount = 0;
    let lastNewId: string | null = null;
    try {
      for (const item of items) {
        patch(item.key, { state: "uploading", progress: 0 });
        try {
          const result = await uploadTranscription(item.file, (f) => patch(item.key, { progress: f }));
          patch(item.key, {
            state: result.deduplicated ? "duplicate" : "done",
            progress: 1,
            result,
          });
          if (!result.deduplicated) {
            uploadedCount++;
            lastNewId = result.id;
          }
        } catch (err) {
          patch(item.key, { state: "error", error: (err as Error).message });
        }
      }
    } finally {
      busyRef.current = false;
    }
    if (uploadedCount === 1 && items.length === 1 && lastNewId) {
      onUploaded(lastNewId);
    } else {
      onChanged?.();
    }
  }, [onUploaded, onChanged]);

  const accept = useCallback((files: FileList | File[]) => {
    const fresh: QueueItem[] = Array.from(files)
      .filter((f) => f.size > 0)
      .map((file) => ({ key: `u${keySeq++}`, file, state: "waiting" as const, progress: 0 }));
    if (fresh.length === 0) return;
    setQueue((q) => [...q.filter((it) => it.state === "uploading" || it.state === "waiting"), ...fresh]);
    void pump(fresh);
  }, [pump]);

  const active = queue.some((it) => it.state === "uploading" || it.state === "waiting");

  return (
    <section aria-label="Upload recordings">
      <div
        className={`submit-block ${drag ? "drag" : ""}`}
        onDragEnter={(e) => { e.preventDefault(); setDrag(true); }}
        onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
        onDragLeave={(e) => { e.preventDefault(); setDrag(false); }}
        onDrop={(e) => {
          e.preventDefault();
          setDrag(false);
          if (e.dataTransfer.files.length) accept(e.dataTransfer.files);
        }}
      >
        <div className="submit-prompt">
          <h2>Submit recordings</h2>
          <span className="hint">{active ? "transcription starts on arrival" : "drop audio here, or click to browse"}</span>
        </div>
        <input
          type="file"
          accept="audio/*,video/*"
          multiple
          aria-label="Choose audio files to transcribe"
          onChange={(e) => {
            if (e.target.files?.length) accept(e.target.files);
            e.target.value = "";
          }}
        />
      </div>

      {queue.length > 0 && (
        <ul className="upload-queue" aria-live="polite">
          {queue.map((it) => (
            <li key={it.key} className={`upload-row ${it.state}`}>
              <span className="upload-name">{it.file.name}</span>
              <span className="upload-size num">{fmtBytes(it.file.size)}</span>
              <span className="upload-state">
                {it.state === "waiting" && "waiting"}
                {it.state === "uploading" && `${Math.round(it.progress * 100)}%`}
                {it.state === "done" && it.result && (
                  <Link to={`/transcriptions/${it.result.id}`}>queued — view</Link>
                )}
                {it.state === "duplicate" && it.result && (
                  <Link to={`/transcriptions/${it.result.id}`}>already transcribed — open</Link>
                )}
                {it.state === "error" && <span className="upload-error" role="alert">{it.error}</span>}
              </span>
              {it.state === "uploading" && (
                <span className="upload-progress" style={{ ["--p" as never]: it.progress }} aria-hidden="true" />
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
